import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { getPointsBulk, getSignals } from '../api';
import { day } from '../format';
import TrendTile from './TrendTile';
import { useWorkspace } from './Workspace';

/* The front page. A short list on purpose: most weeks most things are quiet,
 * and the value of the list is that everything on it earned its place. */

export default function TrendsHome() {
  const { cov, run, openSearch } = useWorkspace();
  const [signals, setSignals] = useState([]);
  const [curves, setCurves] = useState({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();

  /* A quiet week and a broken request are not the same thing, and this used to
   * render them identically: the catch set an empty list and the page then
   * announced that nothing had broken out. It said that for a fortnight
   * against an API that was answering 404 to every call. */
  useEffect(() => {
    getSignals()
      .then((sig) => {
        setSignals(sig);
        setFailed(false);
        const ids = sig.map((s) => s.series_id).filter(Boolean);
        if (ids.length) getPointsBulk(ids).then(setCurves).catch(() => setCurves({}));
      })
      .catch(() => {
        setSignals([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  /* "New this week" means new to US, which is not the same as newly begun and
   * cannot be asked of started_on. The break is dated from the curve, so the
   * first time we poll a term we look back five years and report truthfully
   * that its run began in May - an episode is routinely months old on the day
   * it first appears here. first_seen_on is the week we actually recorded it,
   * and it is the only column that answers "what wasn't here last week".
   *
   * Held in the URL rather than in state so that opening a trend and coming
   * back lands on the same list, which is also what the restored scroll
   * position is measured against. */
  const week = run?.week_end ? String(run.week_end).slice(0, 10) : null;
  const fresh = useMemo(
    () => (week ? signals.filter((s) => String(s.first_seen_on || '').slice(0, 10) === week) : []),
    [signals, week],
  );

  const onlyNew = params.get('new') === '1';
  const setFilter = (next) => setParams(next ? { new: '1' } : {}, { replace: true });

  /* The workspace used to hold its open view in a query param on this one path.
   * Anything already shared or bookmarked still arrives that way, so it is
   * translated rather than dropped on the floor. */
  const legacy = ['e', 'c', 's'].map((k) => [k, Number(params.get(k))]).find(([, id]) => id);
  if (legacy) {
    const [key, id] = legacy;
    const to = { e: `/trends/${id}`, c: `/companies/${id}`, s: `/terms/${id}` }[key];
    return <Navigate to={to} replace />;
  }

  const shown = onlyNew ? fresh : signals;

  return (
    <div className="tw-page">
      <div className="tw-page-head">
        <div>
          <h1 className="tw-page-title">Open trends</h1>
          {/* Two claims, both of which have to stay true of what is actually
            * rendered. Every open episode is written up by a model and scored,
            * and /api/signals orders on that score - so this says "ranked",
            * never "vetted". A low score is a verdict the reader can see, not
            * one we act on for them. The one thing held back is a zero, which
            * is the model saying the move is not this company's demand at all,
            * so there is no judgement left for the reader to make. */}
          <p className="tw-page-sub">
            Search terms that have broken out of their own normal range and are
            still there. Each one is read by a language model that explains what
            is driving it and scores how material it is to the company, and the
            list is ordered by that score.
            {run?.week_end ? ` Week ending ${day(run.week_end)}.` : ''}
          </p>
        </div>
        {!loading && !failed && signals.length > 0 && (
          <div className="tw-seg" role="group" aria-label="Filter trends">
            <button
              type="button"
              className={`tw-seg-btn${onlyNew ? '' : ' is-on'}`}
              onClick={() => setFilter(false)}
            >
              All <span className="tw-seg-n">{signals.length}</span>
            </button>
            <button
              type="button"
              className={`tw-seg-btn${onlyNew ? ' is-on' : ''}`}
              disabled={!fresh.length}
              onClick={() => setFilter(true)}
            >
              New this week <span className="tw-seg-n">{fresh.length}</span>
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="tw-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div className="tw-tile is-loading" key={i}>
              <div className="tw-shimmer" style={{ width: '55%' }} />
              <div className="tw-shimmer" style={{ width: '35%', height: 8 }} />
              <div className="tw-shimmer" style={{ width: '100%', height: 28 }} />
            </div>
          ))}
        </div>
      )}

      {!loading && failed && (
        <div className="tw-empty">
          <div>
            <strong>We could not load this week&rsquo;s trends.</strong> This is a
            fault at our end, not a quiet week.
          </div>
          <div>
            The list is still there. Reload, and if it keeps happening tell us at
            xoxo@inflection.pro and we will go and look.
          </div>
          <button type="button" className="cta" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      )}

      {!loading && !failed && signals.length === 0 && (
        <div className="tw-empty">
          <div>
            <strong>Nothing is open.</strong> No term has broken out of its normal
            range and held there.
          </div>
          <div>
            That is the usual state of the world, and it is why this page is
            short. Everything we cover stays searchable
            {cov
              ? ` — ${cov.companies.toLocaleString()} companies and ${cov.series.toLocaleString()} weekly terms.`
              : '.'}
          </div>
          <button type="button" className="cta" onClick={openSearch}>
            Search the universe
          </button>
        </div>
      )}

      {/* Reachable by URL even when the button that sets it is disabled, so it
        * has to say something rather than render an empty grid. */}
      {!loading && !failed && signals.length > 0 && shown.length === 0 && (
        <div className="tw-empty">
          <div>
            <strong>Nothing new this week.</strong> Every open trend was already
            on the list before this week&rsquo;s run.
          </div>
          <button type="button" className="cta" onClick={() => setFilter(false)}>
            Show all {signals.length}
          </button>
        </div>
      )}

      <div className="tw-grid">
        {shown.map((s) => (
          <TrendTile key={s.episode_id} signal={s} points={curves[s.series_id]} />
        ))}
      </div>
    </div>
  );
}
